import { ButtonHTMLAttributes, ReactNode, forwardRef } from "react";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

import { cn } from "../../utls/utils";

export type EditButtonTone = "terracotta" | "neutral";

const toneClasses: Record<EditButtonTone, string> = {
  terracotta:
    "border-terracotta-200 bg-cream-50 text-terracotta-700 hover:bg-cream-100 focus-visible:ring-terracotta-500",
  neutral:
    "border-beige-200 bg-white text-brown-700 hover:bg-beige-50 focus-visible:ring-brown-500",
};

const iconToneClasses: Record<EditButtonTone, string> = {
  terracotta: "bg-terracotta-50 text-terracotta-600",
  neutral: "bg-beige-100 text-brown-600",
};

export interface EditButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  /** Visual tone used for the border and hover styles. */
  tone?: EditButtonTone;
  /** Optional text label rendered next to the icon. */
  children?: ReactNode;
  /** Icon rendered inside the circular badge. */
  icon?: ReactNode;
  /** Accessible label when no visible text is provided. */
  ariaLabel?: string;
}

export const EditButton = forwardRef<HTMLButtonElement, EditButtonProps>(
  function EditButton(
    {
      tone = "terracotta",
      children,
      icon = <PencilSquareIcon className="h-4 w-4" aria-hidden />,
      className,
      type = "button",
      ariaLabel,
      ...rest
    },
    ref
  ) {
    const resolvedAriaLabel =
      ariaLabel ?? (children ? undefined : "Edit details");

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          toneClasses[tone],
          className
        )}
        aria-label={resolvedAriaLabel}
        {...rest}
      >
        <span
          aria-hidden
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full border border-transparent transition-colors group-hover:border-current",
            iconToneClasses[tone]
          )}
        >
          {icon}
        </span>
        {children ? (
          <span className="text-current">{children}</span>
        ) : (
          <span className="sr-only">Edit</span>
        )}
      </button>
    );
  }
);

EditButton.displayName = "EditButton";

export default EditButton;
